import React from 'react';
import {
    PROPTYPE_AIRPORT_SHUTTLE,
    PROPTYPE_AIRPORT_DETAILS,
} from '@/proptypes/reservation-prop-types';
import {rightColumnItemStyleProps} from 'pages/parking-pass/styles';
import FormatUtils from 'utils/format';

import {Flex, Heading, Link, Text, Box} from '@spothero/ui';
import ReservationUtils from 'utils/reservation-utils';
import useMobileBreakPoint from 'hooks/use-mobile-breakpoint';
import SpotImages from 'common/spot-details/spot-images';
import {CarouselVariant} from 'common/image-carousel';

export const RIDE_SHARE_TEXTS = {
    title: 'Rideshare/taxi Information',
    content:
        'This is a rideshare or taxi location. You will have to arrange transport to and from this spot.',
};

export const PUBLIC_TRANSIT_TEXTS = {
    title: 'Public Transit Information',
    content:
        'This is a public transit location. You can get to and from the airport via public transit.',
};

const ShuttleInformation = ({shuttle, details}) => {
    const isMobile = useMobileBreakPoint();

    if (!shuttle) {
        return null;
    }

    const {
        hours,
        fastFrequency,
        slowFrequency,
        contactPhoneNumber,
        duration,
        type,
        images,
    } = shuttle;

    const is247 = hours ? hours.text?.length > 0 : false;
    const showHours = is247 || Boolean(hours?.periods?.length);

    if (type === ReservationUtils.SHUTTLE_TYPES.CURBSIDE_VALET) {
        return null;
    }

    if (
        type === ReservationUtils.SHUTTLE_TYPES.RIDE_SHARE ||
        type === ReservationUtils.SHUTTLE_TYPES.PUBLIC_TRANSIT
    ) {
        const {title, content} =
            type === ReservationUtils.SHUTTLE_TYPES.RIDE_SHARE
                ? RIDE_SHARE_TEXTS
                : PUBLIC_TRANSIT_TEXTS;

        return (
            <Flex
                {...rightColumnItemStyleProps}
                alignItems="flex-start"
                paddingBottom="5"
            >
                <Heading
                    lineHeight="1.4"
                    as="h3"
                    fontSize="base"
                    marginBottom={2}
                >
                    {title}
                </Heading>
                <Text>{content}</Text>
            </Flex>
        );
    }

    const showScheduledShuttleFrequency = () => {
        if (fastFrequency && slowFrequency) {
            return `Every ${fastFrequency} \u2013 ${slowFrequency} min`;
        } else if (fastFrequency && !slowFrequency) {
            return `Every ${fastFrequency} min`;
        } else if (!fastFrequency && slowFrequency) {
            return `Every ${slowFrequency} min`;
        } else {
            return '';
        }
    };

    return (
        <Flex {...rightColumnItemStyleProps} alignItems="flex-start">
            <Heading as="h3" fontSize="base" marginBottom={2} fontWeight="bold">
                Shuttle Information
            </Heading>
            <Flex flexDirection="column" width="100%" gap="2">
                {Boolean(images?.length) && (
                    <Box
                        marginTop="1"
                        marginBottom="3"
                        sx={{
                            '.ImageCarousal-image-count': {
                                left: '90%',
                                top: '2',
                                bottom: 'initial',
                            },
                        }}
                    >
                        <SpotImages
                            images={images}
                            alt="shuttle image"
                            urlProperty="urlTemplate"
                            carouselHeight={isMobile ? 15 : 13}
                            carouselVariant={CarouselVariant.SINGLE}
                        />
                    </Box>
                )}
                {showHours && (
                    <Flex
                        justifyContent="space-between"
                        direction={is247 ? 'row' : 'column'}
                        marginBottom={is247 ? '0' : '6'}
                    >
                        <Heading as="h5">Hours</Heading>
                        {is247 ? (
                            <Text>24/7</Text>
                        ) : (
                            hours.periods.map(
                                (
                                    {startDow, endDow, startTime, endTime},
                                    idx
                                ) => (
                                    <Flex
                                        key={`${idx}-${startDow}-${endDow}`}
                                        width="100%"
                                        justifyContent="space-between"
                                        gap="2.5"
                                    >
                                        <Text>
                                            {startDow === endDow
                                                ? startDow
                                                : `${startDow} \u2013 ${endDow}`}
                                        </Text>
                                        <Text>
                                            {`${startTime} \u2013 ${endTime}`}
                                        </Text>
                                    </Flex>
                                )
                            )
                        )}
                    </Flex>
                )}
                {type ? (
                    <Flex justifyContent="space-between" width="100%">
                        <Heading as="h5">Frequency</Heading>
                        {type === ReservationUtils.SHUTTLE_TYPES.SCHEDULED ? (
                            <Text data-testid="ShuttleInformation-sheduled-frequency">
                                {showScheduledShuttleFrequency()}
                            </Text>
                        ) : type ===
                          ReservationUtils.SHUTTLE_TYPES.ON_DEMAND ? (
                            <Text>On demand</Text>
                        ) : (
                            ''
                        )}
                    </Flex>
                ) : (
                    ''
                )}
                {duration && details ? (
                    <Flex justifyContent="space-between" width="100%">
                        <Heading as="h5">Time to {details?.code}</Heading>
                        <Text>{duration} min</Text>
                    </Flex>
                ) : (
                    ''
                )}
                {contactPhoneNumber ? (
                    <Flex justifyContent="space-between" width="100%">
                        <Heading as="h5">Phone Number</Heading>
                        <Link
                            href={`tel:${contactPhoneNumber}`}
                            fontSize="base"
                        >
                            {FormatUtils.phone(contactPhoneNumber)}
                        </Link>
                    </Flex>
                ) : (
                    ''
                )}
            </Flex>
        </Flex>
    );
};

ShuttleInformation.propTypes = {
    shuttle: PROPTYPE_AIRPORT_SHUTTLE,
    details: PROPTYPE_AIRPORT_DETAILS,
};

export default ShuttleInformation;
